import {
  PetsApi,
  PetResponse,
  PetOwnerResponse,
  CreatePetRequest,
  UpdatePetRequest,
  PetListResponse,
  AddPetOwnerRequest,
  UpdatePetOwnerRequest
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

/**
 * Wrapped pets API with standardized error handling
 */
class WrappedPetsApi {
  /**
   * Create a new pet
   */
  async createPet(petData: CreatePetRequest): Promise<ApiResult<PetResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.createPet(petData)
    );
  }

  /**
   * Get a pet by ID
   */
  async getPet(petId: string): Promise<ApiResult<PetResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.getPet(petId)
    );
  }

  /**
   * Update a pet
   */
  async updatePet(petId: string, petData: UpdatePetRequest): Promise<ApiResult<PetResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.updatePet(petId, petData)
    );
  }

  /**
   * Delete a pet
   */
  async deletePet(petId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.deletePet(petId)
    );
  }

  /**
   * Search pets
   */
  async searchPets(familyId: string): Promise<ApiResult<PetListResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.searchPets(familyId)
    );
  }

  /**
   * Get pets by owner family member ID
   */
  async getPetsByOwner(ownerFamilyMemberId: string): Promise<ApiResult<PetListResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.getPetsByOwner(ownerFamilyMemberId)
    );
  }

  /**
   * Add an owner to a pet
   */
  async addPetOwner(petFamilyMemberId: string, body: AddPetOwnerRequest): Promise<ApiResult<PetOwnerResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.addPetOwner(petFamilyMemberId, body)
    );
  }

  /**
   * Remove an owner from a pet
   */
  async removePetOwner(petFamilyMemberId: string, petOwnerId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.removePetOwner(petFamilyMemberId, petOwnerId)
    );
  }

  /**
   * Update a pet owner (role, effective dates)
   */
  async updatePetOwner(
    petFamilyMemberId: string,
    petOwnerId: string,
    body: UpdatePetOwnerRequest
  ): Promise<ApiResult<PetOwnerResponse>> {
    return withApiSetup(
      (config) => new PetsApi(config),
      getApiConfiguration,
      (api) => api.updatePetOwner(petFamilyMemberId, petOwnerId, body)
    );
  }
}

/**
 * Wrapped pets API instance with standardized error handling
 */
export const petsApi = new WrappedPetsApi();

export default petsApi;
